import type { SessionAssignationState } from "@context/sessionAssignation";
import type {
  SessionAssignationGroup,
  SessionAssignationValidated,
} from "@services/types/session-assignation";
import type { Session, SessionsMultiEditPayload } from "@models/ISchema";
import { DAY_BY_ID } from "@services/collections/session-assignation";
import { initialState, InitialStateType } from "../../context/EditSessionsContext";
import type { EditedSession } from "../../context/formData.reducer";
import { resultReducer, Types as ResultTypes } from "../../context/result.reducer";
import sessionAdapter from "../../adapters/session";

export const buildSessionAssignationResults = (
  parent: InitialStateType,
  draft: Pick<SessionAssignationState, "weeks" | "sections" | "originals">,
  validated: SessionAssignationValidated,
): InitialStateType => {
  const weeks = draft.weeks.map((week, index) => ({
    ...week,
    value: parent.link.weeks?.find(item => item.id === week.id)?.value ?? index + 1,
  }));
  const sections = draft.sections.map(section => ({
    ...parent.link.sections?.find(item => item.id === section.id),
    ...section,
  }));
  const sectionById = new Map(sections.map(section => [section.id, section]));
  const originalsById = new Map(
    draft.originals.map(original => {
      const blocks = original.session.assignment?.blocks ?? [];
      const days = Object.values(DAY_BY_ID).filter(day => blocks.some(block => block.day === day));
      const firstDay = blocks
        .filter(block => block.day === days[0])
        .sort((a, b) => a.startingTime.localeCompare(b.startingTime));
      const assignment = {
        ...original.session.assignment,
        days,
        day: days[0] ?? null,
        blockRange: firstDay.length
          ? { start: firstDay[0], end: firstDay[firstDay.length - 1] }
          : null,
      };
      return [
        original.session.id,
        {
          ...original.session,
          assignment,
          section: sectionById.get(original.sectionId),
        } as Session,
      ] as const;
    }),
  );
  const form = {
    ...structuredClone(initialState.form),
    blockingThreshold: parent.form.blockingThreshold,
  };
  const edited = (
    id: string,
    sectionId: string,
    assignment: SessionAssignationGroup,
  ): EditedSession => {
    const days = [...new Set(assignment.blocks.map(block => block.day))];
    const firstDay = days[0];
    const dayBlocks = assignment.blocks
      .filter(block => block.day === firstDay)
      .sort((a, b) => a.startingTime.localeCompare(b.startingTime));
    const original = originalsById.get(id);
    return {
      id,
      section: sectionById.get(sectionId),
      isNew: !originalsById.has(id),
      isCloned: false,
      session: original ? (sessionAdapter(weeks)(original) as Session) : undefined,
      blocks: dayBlocks.length
        ? {
            selected: "hours",
            days,
            day: firstDay,
            startTime: dayBlocks[0].startingTime,
            endTime: dayBlocks[dayBlocks.length - 1].endingTime,
            blocks: dayBlocks.length,
          }
        : { selected: "blocks", blocks: assignment.blocksCount },
      classrooms: [...assignment.classrooms],
      instructors: [...assignment.instructors],
      intervals: weeks.map(week => ({
        ...week,
        checked: assignment.intervalIds.includes(week.id),
      })),
    };
  };
  for (const operation of validated.operations) {
    if (operation.kind === "create")
      form.sessionsToCreate.push(
        edited(operation.correlationId, operation.sectionId, operation.assignment),
      );
    if (operation.kind === "update")
      form.savedSessions.push(
        edited(operation.originalSessionId, operation.sectionId, operation.assignment),
      );
    if (operation.kind === "delete") {
      const original = originalsById.get(operation.originalSessionId);
      form.sessionsToDelete[original.id] = sessionAdapter(weeks)(original);
    }
  }
  const payload = validated.result.payload;
  const validation = (
    sessionsPayload: SessionsMultiEditPayload["sessionsPayload"],
  ): SessionsMultiEditPayload => ({
    sessionsPayload: sessionsPayload.filter(Boolean),
    commited: validated.result.commited,
    skippedValidations: validated.result.skippedValidations,
    userCanSkipValidations: validated.result.userCanSkipValidations,
  });
  let result = resultReducer(null, {
    type: ResultTypes.ResultValidation,
    payload: validation([...(payload.updates ?? []), ...(payload.deletes ?? [])]),
  });
  result = resultReducer(result, {
    type: ResultTypes.CreateValidation,
    payload: validation(payload.creates ?? []),
  });
  const first = validated.operations[0];
  if (first?.kind === "create")
    result = resultReducer(result, {
      type: ResultTypes.SelectCreateSession,
      payload: form.sessionsToCreate.find(item => item.id === first.correlationId),
    });
  if (first?.kind === "update")
    result = resultReducer(result, {
      type: ResultTypes.SelectSession,
      payload: originalsById.get(first.originalSessionId),
    });
  if (first?.kind === "delete")
    result = resultReducer(result, {
      type: ResultTypes.SelectDeletedSession,
      payload: form.sessionsToDelete[first.originalSessionId],
    });
  return {
    ...parent,
    form,
    result,
    page: { ...parent.page, active: { ...parent.page.active, form: false, results: true } },
    link: {
      ...parent.link,
      weeks,
      sections: sections.map(section => ({
        ...section,
        sessions: section.sessions.map(
          session => sessionAdapter(weeks)(originalsById.get(session.id)) as Session,
        ),
        unasignedSessions: (section.unasignedSessions ?? []).map(
          session => sessionAdapter(weeks)(originalsById.get(session.id)) as Session,
        ),
      })),
    },
  };
};
